import type { ReactNode } from 'react';
import { Badge, Button } from '@staff-app/ui';
import { rmaFormFor, rmaNo, type DeviceRmaRequest, type RmaDeviceModel } from '@staff-app/shared-types';
import { ExternalLink, X } from 'lucide-react';
import { CopyButton } from '@/components/CopyButton';

const MODEL_LABEL: Record<RmaDeviceModel, string> = {
  OPTISTICK: 'OptiStick',
  PRO: 'Pro Player',
  PROMAX: 'ProMax Player',
  UNKNOWN: 'Unknown',
};

function fmt(d?: string | null): string {
  if (!d) return '—';
  const parsed = new Date(d);
  return isNaN(+parsed) ? '—' : parsed.toLocaleString();
}

/**
 * Is this worth offering a copy button for?
 *
 * A copy button on a value nobody can use is worse than none: it reports success and puts
 * "(unknown)" or "(no subject)" on the clipboard, which then gets pasted into a ticket and read as
 * an address. Those parenthesised strings are REAL stored values — the ingest writes them wherever
 * a form omitted a NOT NULL field — not a rendering artefact, so the guard has to look at the value
 * itself. Same rule CustomerFacts settled on for its "(None)" owner.
 */
function isCopyable(value?: string | null): value is string {
  return Boolean(value && !/^\(.*\)$/.test(value));
}

/**
 * One labelled value. `wide` spans both columns, for anything that wraps — an address or the
 * customer's own description of the fault.
 */
function Row({
  label,
  value,
  wide,
  mono,
  copyTestId,
  children,
}: {
  label: string;
  value?: string | null;
  wide?: boolean;
  mono?: boolean;
  /** Present ⇒ this value gets a copy button. Absent ⇒ it does not. */
  copyTestId?: string;
  /** Rendered beneath the value, inside the same field — for an action that belongs to it. */
  children?: ReactNode;
}) {
  const copyable = Boolean(copyTestId) && isCopyable(value);

  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={`mt-0.5 whitespace-pre-wrap text-sm${mono ? ' font-mono' : ''}`}>
        {value || '—'}
        {copyable ? (
          <CopyButton value={String(value)} label={label} testId={copyTestId} />
        ) : null}
        {children}
      </dd>
    </div>
  );
}

/**
 * RmaDetailDrawer — everything the customer submitted, for one request.
 *
 * WHY THIS EXISTS. The table is a QUEUE: it carries the handful of fields that decide which row to
 * work next, and deliberately leaves out the long ones. But the moment an operator picks a row they
 * need the opposite — the full ship-to address, the fault description in the customer's own words,
 * the acknowledgement they ticked. Sending them to HubSpot for that would undo the point of having
 * the ledger.
 *
 * READ-ONLY, and that is not an omission. Every field here belongs to the HubSpot submission and is
 * refreshed by the poll, so an edit made in this panel would be silently overwritten within five
 * minutes. The one thing staff-app owns — the eligibility verification — is deliberately left on the
 * row in the table, next to the indicator that shows its state, rather than duplicated here where
 * two controls could disagree about which is authoritative.
 *
 * Same shell as OrderDetailDrawer (fixed overlay, right-hand aside, click-outside to close), so it
 * behaves like the drawer staff already know from Shopify Orders.
 */
export function RmaDetailDrawer({
  row,
  onClose,
  onDismiss,
}: {
  row: DeviceRmaRequest;
  onClose: () => void;
  /**
   * Opens the dismiss/restore dialog. It lives HERE rather than in the verify dialog because
   * dismissing is not part of the eligibility decision — it is the judgement that this request
   * should not be worked at all, which is made while reading what the customer actually sent.
   */
  onDismiss?: () => void;
}) {
  const model = row.deviceModel as RmaDeviceModel;
  const form = rmaFormFor(row.sourceFormId);
  const fromForm = Boolean(form) && form?.model === model;

  // The address as one block rather than six rows: it is one fact, and an operator reads it the way
  // it will be printed on a label.
  const block = (a: {
    name?: string | null;
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    country?: string | null;
  }) =>
    [a.name, a.line1, a.line2, [a.city, a.state, a.zip].filter(Boolean).join(', '), a.country]
      .filter((l) => l && String(l).trim())
      .join('\n');

  const address = block({
    name: row.shipToName,
    line1: row.shipToLine1,
    line2: row.shipToLine2,
    city: row.shipToCity,
    state: row.shipToState,
    zip: row.shipToZip,
    country: row.shipToCountry,
  });

  // All-or-nothing server-side, so line1 alone is a reliable marker for "there is a correction".
  const correctedAddress = row.verifiedShipToLine1
    ? block({
        name: row.verifiedShipToName,
        line1: row.verifiedShipToLine1,
        line2: row.verifiedShipToLine2,
        city: row.verifiedShipToCity,
        state: row.verifiedShipToState,
        zip: row.verifiedShipToZip,
        country: row.verifiedShipToCountry,
      })
    : null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <aside
        className="h-full w-full max-w-xl overflow-y-auto bg-background p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="rma-detail-drawer"
      >
        <header className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">{row.subject || '(no subject)'}</h2>
            {/* The customer's address at a glance, kept here BY REQUEST even though the same value
                is a labelled field below. It is the identity line of the panel — who this request
                is from — and an operator reaching for it should not have to scan the grid. Both
                copies are the form's single `email` field; there is no second address. */}
            <p className="text-sm text-muted-foreground">
              {row.customerEmail}
              {isCopyable(row.customerEmail) ? (
                <CopyButton
                  value={row.customerEmail}
                  label="Customer email"
                  testId="rma-drawer-copy-customer-email"
                />
              ) : null}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Badge
            variant={(model === 'UNKNOWN' ? 'destructive' : fromForm ? 'secondary' : 'outline') as never}
            data-testid={`rma-drawer-model-${model}`}
          >
            {MODEL_LABEL[model] ?? model}
          </Badge>
          {/* The state, not a control. Verifying happens on the row in the table, beside the
              indicator that shows it — two controls for one flag invite the question of which
              one won. */}
          <Badge
            variant={(row.verified ? 'secondary' : 'destructive') as never}
            data-testid="rma-drawer-verified"
          >
            {row.verified ? 'Eligibility verified' : 'Not verified'}
          </Badge>
        </div>

        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* First field, matching the queue's first column — the name of the thing you are
              looking at, before anything about it. */}
          <Row label="RMA #" value={rmaNo(row.rmaNumber)} mono copyTestId="rma-drawer-copy-number" />
          <Row label="Submitted" value={fmt(row.submittedAt)} />
          {/* The customer's ACCOUNT email — the form's one `email` field, which HubSpot scopes to
              CONTACT. It is also the header subtitle, but an unlabelled address under a subject is
              not something an operator reads as "the account"; the label is the point. */}
          <Row label="Account email" value={row.customerEmail} copyTestId="rma-drawer-copy-email" />
          {/* The customer's own figure, like the order number above — this panel is the evidence
              view. The verifier's correction, when there is one, gets its own field rather than
              overwriting what was submitted. */}
          <Row label="Devices to return" value={row.deviceCount == null ? null : String(row.deviceCount)} />
          {row.verifiedDeviceCount != null && row.verifiedDeviceCount !== row.deviceCount ? (
            <Row label="Units to replace (verified)" value={String(row.verifiedDeviceCount)} />
          ) : null}
          {/* THE CUSTOMER'S OWN VALUE, deliberately, even though the queue's Order # column shows
              the verifier's correction instead. This panel is the evidence view: it is where
              somebody comes to see what was actually submitted, and the correction has its own
              field below it rather than quietly replacing this one. */}
          <Row label="Order number" value={row.orderNumber} mono copyTestId="rma-drawer-copy-order" />
          {row.previousOrderNumber && row.previousOrderNumber !== row.orderNumber ? (
            <Row
              label="Order number (corrected)"
              value={row.previousOrderNumber}
              mono
              copyTestId="rma-drawer-copy-order-corrected"
            />
          ) : null}
          <Row label="Purchased from" value={row.orderLocation} />

          {/* The replacement, once released. Absent entirely when there is none: an empty
              "Replacement order —" on every unworked row would say nothing and cost a line in the
              panel people scan. */}
          {row.replacementOrderId ? (
            <Row
              label="Replacement order"
              value={row.replacementOrderName || row.replacementOrderId}
              mono
            >
              <div className="mt-2">
                <Button asChild variant="outline" size="sm">
                  <a
                    href={row.replacementOrderUrl ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="rma-drawer-replacement-order"
                    className="gap-1"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {'Open in Shopify'}
                  </a>
                </Button>
              </div>
            </Row>
          ) : null}

          {/* Verbatim and full-width. This is the customer's account of the fault, and it is what a
              verifier reads before deciding anything — truncating it into the table was the reason
              this panel exists. */}
          <Row label="Reported issue" value={row.issueDescription} wide />

          {/* The customer's own, always — this panel is the evidence view. */}
          <Row label="Ship to (submitted)" value={address} wide />
          {/* And where it ACTUALLY goes, when a staffer has corrected it. Shown as its own field
              rather than replacing the one above: when a parcel goes missing, the question is which
              of the two addresses it was sent to, and a panel that had quietly swapped them could
              not answer it. */}
          {correctedAddress ? (
            <Row label="Ship to (corrected — where it ships)" value={correctedAddress} wide>
              <p className="mt-1 text-xs text-muted-foreground" data-testid="rma-drawer-ship-stamp">
                {`Changed by ${row.verifiedShipToBy ?? 'unknown'}${
                  row.verifiedShipToAt ? ` on ${fmt(row.verifiedShipToAt)}` : ''
                }`}
              </p>
            </Row>
          ) : null}

          <Row
            label="Restocking fee acknowledged"
            value={row.restockingAck == null ? null : row.restockingAck ? 'Yes' : 'No'}
          />
          <Row
            label="Verified by"
            value={row.verified ? `${row.verifiedBy ?? 'unknown'} · ${fmt(row.verifiedAt)}` : null}
          />

          <Row label="Form" value={form?.label ?? row.sourceFormId} wide />
          <Row label="Submission id" value={row.submissionId} wide mono copyTestId="rma-drawer-copy-submission" />
          <Row
            label="Return received"
            value={
              row.returnedAt
                ? `${row.returnCondition === 'RECEIVED_INCOMPLETE' ? 'Received, missing parts' : row.returnCondition === 'RECEIVED_TESTING' ? 'Received, testing' : 'Received'} · ${row.returnedBy ?? 'unknown'} · ${fmt(row.returnedAt)}`
                : null
            }
            wide
          />
          <Row
            label="Restocking fee"
            value={
              row.restockingInvoiceId
                ? `${row.restockingFeeMinor != null ? `$${(row.restockingFeeMinor / 100).toFixed(2)}` : '—'} · ${row.restockingInvoicedBy ?? 'unknown'} · ${fmt(row.restockingInvoicedAt)}`
                : null
            }
            wide
          >
            {/* The invoice lives INSIDE the field it belongs to rather than as a loose button at
                the foot of the panel, where it read as an action on the whole request.

                The label no longer says "draft". It is raised as one and staff-app never finalizes
                it — but a human may have, by the time anyone follows this link, so the old wording
                asserted a state the page cannot know. The draft warning stays where it is
                actionable: the moment the fee is raised, in the return dialog. */}
            {row.restockingInvoiceUrl ? (
              <div className="mt-2">
                <Button asChild variant="outline" size="sm">
                  <a
                    href={row.restockingInvoiceUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="rma-drawer-invoice"
                    className="gap-1"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {'Open restocking-fee invoice'}
                  </a>
                </Button>
              </div>
            ) : null}
          </Row>
          <Row label="Last synced" value={fmt(row.lastSyncedAt)} />
        </dl>

        {/* DISMISS / RESTORE — at the foot of the evidence, which is where the judgement belongs:
            you decide a request is a duplicate or not an RMA by reading what the customer actually
            sent, not from the eligibility dialog. A dismissed row shows its verdict here instead of
            the action, so the panel says what happened before offering to undo it. */}
        {onDismiss ? (
          <div className="mt-6 flex flex-col gap-2 rounded-md border p-3" data-testid="rma-drawer-dismiss">
            {row.dismissed ? (
              <>
                <p className="text-sm">
                  {`Dismissed as ${String(row.dismissedReason ?? '')
                    .toLowerCase()
                    .replace(/_/g, ' ')} by ${row.dismissedBy ?? 'unknown'}`}
                  {row.duplicateOfRmaNumber ? ` · duplicate of ${rmaNo(row.duplicateOfRmaNumber)}` : ''}
                </p>
                <p className="text-xs text-muted-foreground">
                  {'Not actionable while dismissed — verification, replacement and the return receipt all refuse on it.'}
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">
                {'Duplicate, test, or not an RMA at all? Take it out of the working queue. It is not deleted — the row stays as the record of what was filed.'}
              </p>
            )}
            <div>
              <Button variant="outline" size="sm" onClick={onDismiss} data-testid="rma-drawer-dismiss-open">
                {row.dismissed ? 'Restore to the queue' : 'Dismiss request'}
              </Button>
            </div>
          </div>
        ) : null}

        {row.proofOfPurchaseUrl ? (
          <div className="mt-6">
            <Button asChild variant="outline" size="sm">
              {/* HubSpot signs these urls and they expire, so this is a plain link rather than an
                  embed — a dead link means open the submission in HubSpot, not that data is lost. */}
              <a
                href={row.proofOfPurchaseUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="rma-drawer-receipt"
                className="gap-1"
              >
                <ExternalLink className="h-4 w-4" />
                {'Open proof of purchase'}
              </a>
            </Button>
          </div>
        ) : (
          <p className="mt-6 text-sm text-muted-foreground" data-testid="rma-drawer-no-receipt">
            {'No proof of purchase was attached.'}
          </p>
        )}
      </aside>
    </div>
  );
}
